import { useCallback, useMemo, useState } from "react";
import { Form, useToast } from "@ir/ui";
import {
  CreateMasterDataRetailerDTO,
  UpdateMasterDataRetailerDTO,
} from "@src/domain/dto/masterData";
import type { MasterDataRetailer } from "@src/domain/entities/masterData";
import {
  CreateMasterDataRetailerUseCase,
  UpdateMasterDataRetailerUseCase,
} from "@src/application/useCases/masterData";
import { MasterDataRetailerRepository } from "@src/infrastructure/repositories/masterData";
import { invalidateMasterDataListCacheScopes } from "@src/presentation/providers/masterData/masterDataListCache";
import { useMasterDataRetailers } from "@src/presentation/providers/MasterDataRetailersDataProvider";
import { MasterDataAddEditModal } from "../utils/MasterDataAddEditModal";
import {
  MasterDataRetailerAddEditForm,
  type RetailerAddEditFormValues,
} from "../utils/MasterDataRetailerAddEditForm";
import { MasterDataRetailerMatchRulesModal } from "../utils/MasterDataRetailerMatchRulesModal";
import { MasterDataRawRetailerAssociationsModal } from "../utils/MasterDataRawRetailerAssociationsModal";
import {
  MasterDataRetailersTable,
  type MasterDataRetailerChildTarget,
} from "../utils/MasterDataRetailersTable";
import { MasterDataWritableListLayout } from "../utils/MasterDataWritableListLayout";
import { getMasterDataSaveErrorMessage } from "../utils/masterDataSaveErrors";
import {
  isRetailerKeyDuplicateError,
  RETAILER_KEY_ALREADY_EXISTS_MESSAGE,
} from "../utils/retailerKeyValidation";
import {
  formatMasterDataRecordCount,
  useMasterDataAddEditModal,
} from "../utils/useMasterDataAddEditModal";

const EMPTY_RETAILER_FORM_VALUES: RetailerAddEditFormValues = {
  retailerName: "",
  retailerKey: "",
};

export const MasterDataRetailersPage = () => {
  const { retailers, loading, search, setSearch, total, setPage, refetch } =
    useMasterDataRetailers();
  const { success: successToast, error: errorToast } = useToast();
  const [form] = Form.useForm<RetailerAddEditFormValues>();

  const {
    modalMode,
    editingEntity: editingRetailer,
    isSaving,
    setIsSaving,
    isFormSubmittable,
    setIsFormSubmittable,
    closeModal: closeModalState,
    openCreateModal,
    openEditModal,
  } = useMasterDataAddEditModal<MasterDataRetailer>();

  const [matchRulesPreview, setMatchRulesPreview] =
    useState<MasterDataRetailerChildTarget | null>(null);
  const [rawAssocPreview, setRawAssocPreview] =
    useState<MasterDataRetailerChildTarget | null>(null);

  const retailerRepository = useMemo(
    () => new MasterDataRetailerRepository(),
    [],
  );
  const createUseCase = useMemo(
    () => new CreateMasterDataRetailerUseCase(retailerRepository),
    [retailerRepository],
  );
  const updateUseCase = useMemo(
    () => new UpdateMasterDataRetailerUseCase(retailerRepository),
    [retailerRepository],
  );

  const initialValues = useMemo((): RetailerAddEditFormValues => {
    if (modalMode === "edit" && editingRetailer) {
      return {
        retailerName: editingRetailer.retailerName,
        retailerKey: editingRetailer.retailerKey,
      };
    }
    return EMPTY_RETAILER_FORM_VALUES;
  }, [editingRetailer, modalMode]);

  const closeModal = useCallback(() => {
    closeModalState(form);
  }, [closeModalState, form]);

  const handleSubmit = useCallback(
    async (values: RetailerAddEditFormValues) => {
      setIsSaving(true);
      try {
        const retailerName = values.retailerName.trim();
        const retailerKey = values.retailerKey.trim();
        if (modalMode === "edit" && editingRetailer) {
          await updateUseCase.execute(
            new UpdateMasterDataRetailerDTO({
              retailerId: editingRetailer.retailerId,
              retailerName,
            }),
          );
        } else {
          await createUseCase.execute(
            new CreateMasterDataRetailerDTO({
              retailerName,
              retailerKey,
            }),
          );
        }

        successToast({
          content: "Retailer saved successfully",
        });
        invalidateMasterDataListCacheScopes(["retailers"]);
        if (modalMode !== "edit") {
          setPage(1);
        }
        closeModal();
        refetch();
      } catch (err) {
        if (isRetailerKeyDuplicateError(err)) {
          form.setFields([
            {
              name: "retailerKey",
              errors: [RETAILER_KEY_ALREADY_EXISTS_MESSAGE],
            },
          ]);
          setIsFormSubmittable(false);
        } else {
          errorToast({
            content: getMasterDataSaveErrorMessage(
              err,
              "Failed to save retailer. Please try again.",
              "Failed to save retailer. Please try again later.",
            ),
          });
        }
      } finally {
        setIsSaving(false);
      }
    },
    [
      closeModal,
      createUseCase,
      editingRetailer,
      errorToast,
      form,
      modalMode,
      refetch,
      setIsFormSubmittable,
      setIsSaving,
      setPage,
      successToast,
      updateUseCase,
    ],
  );

  return (
    <>
      <MasterDataWritableListLayout
        title="Retailers"
        subtitle={formatMasterDataRecordCount(loading, total)}
        searchPlaceholder="Search retailers"
        search={search}
        setSearch={setSearch}
        searchSection="retailers"
        addButtonLabel="Add Retailer"
        onAdd={openCreateModal}
        loading={loading}
      >
        <MasterDataRetailersTable
          retailers={retailers}
          search={search}
          setSearch={setSearch}
          setPage={setPage}
          onEdit={openEditModal}
          onViewMatchRules={setMatchRulesPreview}
          onViewRawAssociations={setRawAssocPreview}
        />
      </MasterDataWritableListLayout>

      <MasterDataAddEditModal
        mode={modalMode}
        createTitle="Add Retailer"
        editTitle="Edit Retailer"
        isSaving={isSaving}
        isFormSubmittable={isFormSubmittable}
        form={form}
        onClose={closeModal}
      >
        <MasterDataRetailerAddEditForm
          form={form}
          mode={modalMode ?? "create"}
          initialValues={initialValues}
          onSubmit={handleSubmit}
          onFormValidityChange={setIsFormSubmittable}
        />
      </MasterDataAddEditModal>

      <MasterDataRetailerMatchRulesModal
        isOpen={matchRulesPreview != null}
        retailerId={matchRulesPreview?.retailerId ?? null}
        retailerName={matchRulesPreview?.retailerName ?? ""}
        onClose={() => setMatchRulesPreview(null)}
      />

      <MasterDataRawRetailerAssociationsModal
        isOpen={rawAssocPreview != null}
        retailerId={rawAssocPreview?.retailerId ?? null}
        retailerName={rawAssocPreview?.retailerName ?? ""}
        onClose={() => setRawAssocPreview(null)}
      />
    </>
  );
};
